import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { difference, intersection } from 'lodash';
import type { Props as BaseProps } from 'feature-components/assessment-filters/base-filter';
import BaseFilter from 'feature-components/assessment-filters/base-filter';
import { Group } from 'feature-components/tables/table/filters/inputs';
import type { BaseEthnicity } from 'services/assist-api/entities/ethnicity/ethnicities';
import { useEthnicities } from 'services/assist-api/entities/ethnicity/ethnicities';
import type { BaseEthnicityGroup } from 'services/assist-api/entities/ethnicity/ethnicity-groups';
import { useEthnicityGroups } from 'services/assist-api/entities/ethnicity/ethnicity-groups';

export type Props = {
  enabledIds?: number[];
  label?: BaseProps<BaseEthnicity, true>['label'];
  value?: readonly BaseEthnicity[];
  filteredIds?: number[];
} & Pick<BaseProps<BaseEthnicity, true>, 'isClearable' | 'isSearchable' | 'onChange'>;

/**
 * Filter that is auto populated by backend defined ethnicities & ethnicity groups
 *
 *  - fetches ethnicity & ethnicity groups
 *  - has loading state until data is fetched
 *  - disables ethnicity not included in `enabledIds`
 *  - only works as a multi select
 *  - can select an ethnicity group by clicking on its label
 */
export default function EthnicityFilter({
  enabledIds,
  label = 'Ethnicity',
  isClearable = true,
  isSearchable = true,
  ...props
}: Props): ReactElement {
  const ethnicities = useEthnicities().data?.json;
  const ethnicityGroups = useEthnicityGroups().data?.json;

  const visibleEthnicityGroups = useMemo(
    () =>
      ethnicityGroups?.filter(
        (g) =>
          !enabledIds?.length ||
          difference(
            intersection(
              g.ethnicities?.map((eth) => eth.id),
              enabledIds
            ),
            props.value?.map(({ id }) => id) || []
          )?.length
      ),
    [ethnicityGroups, enabledIds, props.value]
  );

  // build groups for react-select
  const options = useMemo(
    () =>
      visibleEthnicityGroups?.map((group) => ({
        ...group,
        options: ethnicities?.filter((ethnicity) => ethnicity.ethnicityGroup.id === group.id) || []
      })),
    [visibleEthnicityGroups, ethnicities]
  );

  return (
    <BaseFilter<BaseEthnicity, true, BaseEthnicityGroup & { options: BaseEthnicity[] }>
      label={label}
      options={options}
      getOptionValue={(o): string => String(o.id)}
      getOptionLabel={(o): string => o.name}
      formatGroupLabel={(group): JSX.Element => <b>{group.name}</b>}
      isOptionDisabled={(o): boolean => !!enabledIds?.length && !enabledIds.includes(o.id)}
      isLoading={!ethnicities?.length || !ethnicityGroups?.length}
      isClearable={isClearable}
      isSearchable={isSearchable}
      components={{ Group }}
      hideSelectedOptions
      isMulti
      {...props}
    />
  );
}
